import React, { useState, useEffect, useCallback, useMemo } from "react";
// Import UI components from lift-ui
import { 
  Button, 
  Badge,
  cn,
  DataTable,
  DatePickerDateRange,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  Label,
  SpinnerLoader,
  Switch,
  Input,
  Toastify,
} from "lift-ui";
import { FiltersPopover } from "./SessionFiltersPopover";
import type { TableColumn } from "lift-ui";
import {
  WorkflowRunFilterPayload,
  WorkflowRunsFilteredResponse,
  WorkflowRunDetailResponse,
  getFilteredWorkflowRuns,
  getUsersOrderedBySessions,
  getSessionMemoryJson,
  getSessionData,
  toggleWorkflowDisabled,
} from "../../api";
// Icons from lucide-react
import {
  Play,
  Calendar,
  Clock,
  Loader2,
  Timer,
  Workflow,
  Eye,
  User,
  MessageSquare,
  Cpu,
  Tags,
  Share2,
  Mail,
  Search,
} from "lucide-react";
import { format } from "date-fns";
import { useChatV2 } from "../../context/ChatV2Context";
import { useAuthContext } from "@lift-admin/utils/provider";
import { Markdown } from "../prompt-kit/markdown";

// Helper function to convert cron expression to human-readable format
const cronToHumanReadable = (cron: string): string => {
  if (!cron) return 'Not scheduled';
  
  // Common cron patterns
  const parts = cron.split(' ').filter(p => p.trim());
  if (parts.length < 5) return cron; // Not a valid cron
  
  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;
  
  // Check for common patterns
  // Every minute: * * * * *
  if (minute === '*' && hour === '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    return 'Every minute';
  }
  
  // Every hour: 0 * * * *
  if (minute === '0' && hour === '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    return 'Every hour';
  }
  
  // Every X hours: 0 */X * * *
  const hourIntervalMatch = hour.match(/^\*\/(\d+)$/);
  if (minute === '0' && hourIntervalMatch && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    return `Every ${hourIntervalMatch[1]} hours`;
  }
  
  // Daily at specific time: 0 X * * *
  if (minute.match(/^\d+$/) && hour.match(/^\d+$/) && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    const h = parseInt(hour);
    const m = parseInt(minute);
    const period = h >= 12 ? 'PM' : 'AM';
    const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `Daily at ${displayHour}:${m.toString().padStart(2, '0')} ${period}`;
  }
  
  // Weekly on specific day(s): 0 X * * 0-6
  if (minute.match(/^\d+$/) && hour.match(/^\d+$/) && dayOfMonth === '*' && month === '*' && dayOfWeek.match(/^[0-6](,[0-6])*$/)) {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const dayIndices = dayOfWeek.split(',').map(d => parseInt(d));
    const dayNames = dayIndices.map(d => days[d]).join(', ');
    const h = parseInt(hour);
    const m = parseInt(minute);
    const period = h >= 12 ? 'PM' : 'AM';
    const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `Every ${dayNames} at ${displayHour}:${m.toString().padStart(2, '0')} ${period}`;
  }
  
  // Monthly on specific day: 0 X Y * *
  if (minute.match(/^\d+$/) && hour.match(/^\d+$/) && dayOfMonth.match(/^\d+$/) && month === '*' && dayOfWeek === '*') {
    const h = parseInt(hour);
    const m = parseInt(minute);
    const d = parseInt(dayOfMonth);
    const period = h >= 12 ? 'PM' : 'AM';
    const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
    const suffix = d === 1 || d === 21 || d === 31 ? 'st' : d === 2 || d === 22 ? 'nd' : d === 3 || d === 23 ? 'rd' : 'th';
    return `Monthly on the ${d}${suffix} at ${displayHour}:${m.toString().padStart(2, '0')} ${period}`;
  }
  
  // Default: return formatted cron
  return `Cron: ${cron}`;
};

interface WorkflowRunsPanelProps {
  onRunSelect?: (run: WorkflowRunDetailResponse) => void;
  selectedRun?: WorkflowRunDetailResponse | null;
}

const WorkflowRunsPanel: React.FC<WorkflowRunsPanelProps> = ({
  onRunSelect,
  selectedRun,
}) => {
  const [filters, setFilters] = useState<WorkflowRunFilterPayload>({
    page: 1,
    page_size: 10000, // Fetch all, then paginate client-side
  });
  const [runs, setRuns] = useState<WorkflowRunsFilteredResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<string[]>([]);
  
  // Local pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Filter states (arrays for multi-select)
  const [filterUserId, setFilterUserId] = useState<string[]>([]);
  const [filterTriggerType, setFilterTriggerType] = useState<string[]>([]);
  const [filterStartDate, setFilterStartDate] = useState<Date | undefined>(undefined);
  const [filterEndDate, setFilterEndDate] = useState<Date | undefined>(undefined);
  // Free-text search across workflow name + workflow_id + creator name.
  // Mirrors the SessionsPanel pattern so users get a consistent admin
  // experience across panels.
  const [searchQuery, setSearchQuery] = useState<string>("");

  // Sheet state
  const [sheetOpen, setSheetOpen] = useState(false);
  const [selectedWorkflowRun, setSelectedWorkflowRun] = useState<WorkflowRunDetailResponse | null>(null);
  const [loadingWorkflowData, setLoadingWorkflowData] = useState(false);
  const [workflowSessionData, setWorkflowSessionData] = useState<any>(null);
  const [threadHistory, setThreadHistory] = useState<any[]>([]);
  const [expandedThreads, setExpandedThreads] = useState<Set<number>>(new Set());
  const [workflowResponse, setWorkflowResponse] = useState<string>('');

  // Track which workflows are being toggled (loading state)
  const [togglingWorkflows, setTogglingWorkflows] = useState<Set<string>>(new Set());
  // Track local overrides for is_disabled (optimistic UI)
  const [disabledOverrides, setDisabledOverrides] = useState<Record<string, boolean>>({});

  const { userMap } = useChatV2();
  const { canManage } = useAuthContext();
  const hasManageAccess = canManage("lift_ai");

  // Handle toggling workflow enabled/disabled
  const handleToggleDisabled = useCallback(async (workflowId: string, currentlyDisabled: boolean) => {
    if (!hasManageAccess) {
      Toastify("error", "Only managers can enable/disable workflows. Contact your administrator for access.");
      return;
    }

    const newDisabled = !currentlyDisabled;
    
    // Optimistic update
    setDisabledOverrides(prev => ({ ...prev, [workflowId]: newDisabled }));
    setTogglingWorkflows(prev => new Set(prev).add(workflowId));
    
    try {
      await toggleWorkflowDisabled(workflowId, newDisabled);
    } catch (error) {
      // Revert optimistic update on failure
      setDisabledOverrides(prev => {
        const next = { ...prev };
        delete next[workflowId];
        return next;
      });
      console.error("Failed to toggle workflow:", error);
    } finally {
      setTogglingWorkflows(prev => {
        const next = new Set(prev);
        next.delete(workflowId);
        return next;
      });
    }
  }, [hasManageAccess]);

  // Fetch workflow runs
  const fetchRuns = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getFilteredWorkflowRuns(filters);
      setRuns(data);
    } catch (error) {
      console.error("Error fetching workflow runs:", error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Fetch users for filter dropdown
  const fetchUsers = useCallback(async () => {
    try {
      const data = await getUsersOrderedBySessions(100);
      setUsers(data);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  }, []);

  useEffect(() => {
    fetchRuns();
  }, [fetchRuns]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Get unique users from runs
  const uniqueUsers = useMemo(() => {
    if (!runs?.data) return [];
    const userIds = new Set<string>();
    runs.data.forEach((run) => {
      if (run.workflow_created_by) {
        userIds.add(run.workflow_created_by.toString());
      }
    });
    return Array.from(userIds).sort((a, b) => {
      const nameA = userMap?.[a] || a;
      const nameB = userMap?.[b] || b;
      return nameA.localeCompare(nameB);
    });
  }, [runs?.data, userMap]);

  // Client-side filtered and sorted runs
  const filteredRuns = useMemo(() => {
    if (!runs?.data || runs.data.length === 0) return [];
    
    let filtered = [...runs.data];

    // Apply user filter
    if (filterUserId.length > 0) {
      filtered = filtered.filter((run) => 
        filterUserId.includes(run.workflow_created_by?.toString() || '')
      );
    }

    // Apply trigger type filter
    if (filterTriggerType.length > 0) {
      filtered = filtered.filter((run) => 
        filterTriggerType.includes(run.trigger_type || '')
      );
    }

    // Apply date range filter
    if (filterStartDate) {
      filtered = filtered.filter((run) => {
        const runDate = run.workflow_session_created_at 
          ? new Date(run.workflow_session_created_at)
          : null;
        return runDate && runDate >= filterStartDate;
      });
    }

    if (filterEndDate) {
      filtered = filtered.filter((run) => {
        const runDate = run.workflow_session_created_at 
          ? new Date(run.workflow_session_created_at)
          : null;
        if (!runDate) return false;
        const endOfDay = new Date(filterEndDate);
        endOfDay.setHours(23, 59, 59, 999);
        return runDate <= endOfDay;
      });
    }

    // Free-text search runs LAST so it composes with all other filters.
    // Search across workflow name + workflow_id + creator display name.
    const trimmedSearch = searchQuery.trim().toLowerCase();
    if (trimmedSearch) {
      filtered = filtered.filter((run) => {
        const creatorId = run.workflow_created_by?.toString() || '';
        const creatorName = userMap?.[creatorId] || '';
        const haystack = [
          run.workflow_name || '',
          run.workflow_id || '',
          creatorName,
        ].join(' ').toLowerCase();
        return haystack.includes(trimmedSearch);
      });
    }

    // Sort by date created (descending)
    return filtered.sort((a, b) => {
      const dateA = a.workflow_session_created_at 
        ? new Date(a.workflow_session_created_at).getTime()
        : 0;
      const dateB = b.workflow_session_created_at 
        ? new Date(b.workflow_session_created_at).getTime()
        : 0;
      return dateB - dateA;
    });
  }, [runs?.data, filterUserId, filterTriggerType, filterStartDate, filterEndDate, searchQuery, userMap]);

  // Paginated data
  const paginatedData = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return filteredRuns.slice(start, end);
  }, [filteredRuns, page, rowsPerPage]);

  const totalPages = Math.ceil(filteredRuns.length / rowsPerPage);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [filterUserId, filterTriggerType, filterStartDate, filterEndDate, searchQuery]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (size: string) => {
    setRowsPerPage(Number(size));
    setPage(0);
  };

  const handleViewWorkflow = async (run: WorkflowRunDetailResponse) => {
    setSelectedWorkflowRun(run);
    setSheetOpen(true);
    setLoadingWorkflowData(true);
    setWorkflowSessionData(null);
    setThreadHistory([]);
    setExpandedThreads(new Set());
    setWorkflowResponse('');
    // Removed onRunSelect call - only opening sheet component

    // Fetch session data for thread history
    if (run.session_id) {
      try {
        const memoryData = await getSessionMemoryJson(run.session_id);
        const basicData = await getSessionData(run.session_id);
        setWorkflowSessionData({ memory: memoryData, basic: basicData });

        // Extract thread history from memory data and find workflow response
        const threads: any[] = [];
        let lastWorkflowResponse = '';
        
        if (memoryData?.runs && Array.isArray(memoryData.runs)) {
          memoryData.runs.forEach((runItem: any, index: number) => {
            if (runItem.message && runItem.message.content) {
              // Skip the first item if it contains workflow_execution tags
              const content = runItem.message.content;
              const isWorkflowExecution = content.includes('<workflow_execution>') || 
                                         content.includes('<context>') ||
                                         content.includes('scheduled automated workflow execution');
              
              // Skip the first workflow execution message
              if (index === 0 && isWorkflowExecution) {
                // But capture its response as the workflow response
                if (runItem.response && runItem.response.content) {
                  lastWorkflowResponse = runItem.response.content;
                }
                return;
              }
              
              threads.push({
                index: index + 1,
                userMessage: runItem.message.content,
                assistantResponse: runItem.response?.content || '',
                timestamp: runItem.message.timestamp || runItem.created_at,
              });
              
              // Update workflow response to the latest assistant response
              if (runItem.response && runItem.response.content) {
                lastWorkflowResponse = runItem.response.content;
              }
            }
          });
        }
        
        // Set workflow response
        setWorkflowResponse(lastWorkflowResponse);
        
        // Also try to extract thread history from workflow_prompt if it has formatted_history
        if (run.workflow_prompt) {
          try {
            const trimmed = run.workflow_prompt.trim();
            let parsedPrompt: any = null;
            
            // Try to parse as JSON
            if (trimmed.startsWith('{')) {
              parsedPrompt = JSON.parse(trimmed);
            } else {
              // Try to extract from XML format
              const userPromptMatch = run.workflow_prompt.match(/<user_prompt>\s*([\s\S]*?)\s*<\/user_prompt>/);
              if (userPromptMatch) {
                const extracted = userPromptMatch[1].trim();
                if (extracted.startsWith('{')) {
                  parsedPrompt = JSON.parse(extracted);
                }
              }
            }
            
            // Extract formatted_history if it exists
            if (parsedPrompt?.formatted_history && Array.isArray(parsedPrompt.formatted_history)) {
              const queryMap: Record<number, string> = {};
              const answerMap: Record<number, string> = {};
              
              parsedPrompt.formatted_history.forEach((item: any) => {
                if (!item || typeof item !== 'object') return;
                
                Object.keys(item).forEach((key) => {
                  const queryMatch = key.match(/^query(\d+)$/);
                  const answerMatch = key.match(/^answer(\d+)$/);
                  
                  if (queryMatch) {
                    const idx = parseInt(queryMatch[1], 10);
                    queryMap[idx] = item[key];
                  } else if (answerMatch) {
                    const idx = parseInt(answerMatch[1], 10);
                    answerMap[idx] = item[key];
                  }
                });
              });
              
              // Create thread pairs from query/answer maps
              const indices = [...new Set([...Object.keys(queryMap).map(Number), ...Object.keys(answerMap).map(Number)])].sort((a, b) => a - b);
              indices.forEach((idx) => {
                if (queryMap[idx] || answerMap[idx]) {
                  threads.push({
                    index: idx,
                    userMessage: queryMap[idx] || '',
                    assistantResponse: answerMap[idx] || '',
                    timestamp: null,
                  });
                }
              });
            }
          } catch (e) {
            // If parsing fails, continue with memory data threads
            console.log('Could not parse workflow_prompt for thread history:', e);
          }
        }
        
        setThreadHistory(threads);
      } catch (error) {
        console.error('Error fetching workflow session data:', error);
      } finally {
        setLoadingWorkflowData(false);
      }
    } else {
      setLoadingWorkflowData(false);
    }
  };

  const toggleThread = (index: number) => {
    const newExpanded = new Set(expandedThreads);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedThreads(newExpanded);
  };

  const collapseAllThreads = () => {
    setExpandedThreads(new Set());
  };

  // Define columns for DataTable
  const columns: TableColumn<any>[] = useMemo(() => {
    const cols: TableColumn<any>[] = [
    {
      key: "workflow_name",
      header: "Workflow Name",
      accessor: (row: any) => row.workflow_name || "Unnamed Workflow",
      width: hasManageAccess ? "20%" : "24%",
      render: (_v: any, row: any) => (
        <div className="font-medium text-[#242424] max-w-xs">
          <div className="flex items-center gap-2">
            <Workflow className="h-4 w-4 text-[#585755]" />
            <div className="truncate" title={row.workflow_name || "Unnamed Workflow"}>
              {row.workflow_name || "Unnamed Workflow"}
          </div>
          </div>
      </div>
      ),
    },
    ...(hasManageAccess ? [{
      key: "is_disabled",
      header: "Enabled",
      accessor: (row: any) => row.is_disabled,
      width: "8%",
      align: "center" as const,
      render: (_v: any, row: any) => {
        const workflowId = row.workflow_id;
        const isDisabled = disabledOverrides[workflowId] !== undefined
          ? disabledOverrides[workflowId]
          : (row.is_disabled ?? false);
        const isToggling = togglingWorkflows.has(workflowId);

        return (
          <div className="flex justify-center items-center" onClick={(e) => e.stopPropagation()}>
            <Switch
              checked={!isDisabled}
              onCheckedChange={() => handleToggleDisabled(workflowId, isDisabled)}
              disabled={isToggling}
              className={cn(
                "data-[state=checked]:bg-green-500 data-[state=unchecked]:bg-gray-300",
                isToggling && "opacity-50 cursor-wait"
              )}
            />
          </div>
        );
      },
    }] : []),
    {
      key: "trigger_type",
      header: "Scheduled/Manual",
      accessor: (row: any) => row.trigger_type || "scheduled",
      width: "12%",
      render: (_v: any, row: any) => {
        const isScheduled = row.trigger_type === "scheduled";
        return (
          <div className={cn(
            "flex items-center gap-1.5 px-3 py-1.5 rounded-md w-fit",
            isScheduled 
              ? "bg-[#F5F5F4] border border-[#E5E5E4]"
              : "bg-orange-50 border border-orange-200"
          )}
          >
            {isScheduled ? (
              <Timer className="h-3.5 w-3.5 text-[#57534E]" />
            ) : (
              <Play className="h-3.5 w-3.5 text-orange-700" />
            )}
                  <span className={cn(
              "text-xs font-medium",
              isScheduled ? "text-[#57534E]" : "text-orange-700"
                  )}>
              {isScheduled ? "Scheduled" : "Manual"}
                  </span>
          </div>
        );
      },
    },
    {
      key: "date_created",
      header: "Date Created",
      accessor: (row: any) => row.workflow_session_created_at,
      width: "15%",
      render: (_v: any, row: any) => {
        if (!row.workflow_session_created_at) return <span className="text-sm text-[#585755]">-</span>;
        const createdDate = new Date(row.workflow_session_created_at);
        return (
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1 text-sm text-[#242424]">
              <Calendar className="h-3.5 w-3.5 text-[#585755]" />
              <span>{format(createdDate, "MMM dd, yyyy")}</span>
            </div>
            <div className="flex items-center gap-1 text-xs text-[#585755]">
              <Clock className="h-3 w-3" />
              <span>{format(createdDate, "HH:mm")}</span>
            </div>
          </div>
        );
      },
    },
    {
      key: "schedule",
      header: "Schedule",
      accessor: (row: any) => row.workflow_schedule || "",
      width: "20%",
      render: (_v: any, row: any) => {
        if (!row.workflow_schedule) {
          return <span className="text-sm text-[#585755]">Not scheduled</span>;
        }
        return (
          <div className="flex items-center gap-1.5 px-2 py-1 bg-[#F7F4F2] rounded-lg w-fit">
            <Clock className="h-3.5 w-3.5 text-[#585755]" />
            <span className="text-xs text-[#585755]" title={row.workflow_schedule}>
              {cronToHumanReadable(row.workflow_schedule)}
                  </span>
          </div>
        );
      },
    },
    {
      key: "created_by",
      header: "Created By",
      accessor: (row: any) => row.workflow_created_by,
      width: "12%",
      render: (_v: any, row: any) => {
        if (!row.workflow_created_by) {
          return <span className="text-sm text-[#585755]">-</span>;
        }
        const userId = row.workflow_created_by.toString();
        return (
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-[#585755]" />
            <span className="text-sm text-[#242424]">
              {userMap?.[userId] || `User ${userId}`}
                  </span>
          </div>
        );
      },
    },
    {
      key: "shared_to",
      header: "Shared To",
      accessor: (row: any) => row.shared_user_ids || [],
      width: "13%",
      render: (_v: any, row: any) => {
        const sharedUserIds = row.shared_user_ids || [];
        const notify = row.workflow_notify;
        if ((!Array.isArray(sharedUserIds) || sharedUserIds.length === 0) && (!notify || notify === 'none')) {
          return <span className="text-sm text-muted-foreground">-</span>;
        }
        
        // Limit display to first 2 users, show count if more
        const displayUsers = Array.isArray(sharedUserIds) ? sharedUserIds.slice(0, 2) : [];
        const remainingCount = Array.isArray(sharedUserIds) ? Math.max(0, sharedUserIds.length - 2) : 0;
        
        return (
          <div className="flex flex-col gap-1">
            {/* Notification method badges */}
            {notify && notify !== 'none' && (
              <div className="flex items-center gap-1 flex-wrap">
                {(notify === 'email' || notify === 'both') && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-emerald-50 border-emerald-200 text-emerald-700">
                    <Mail className="h-2.5 w-2.5 mr-0.5 inline" />Email
                  </Badge>
                )}
                {(notify === 'slack' || notify === 'both') && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-purple-50 border-purple-200 text-purple-700">
                    <MessageSquare className="h-2.5 w-2.5 mr-0.5 inline" />Slack
                  </Badge>
                )}
              </div>
            )}
            {displayUsers.map((userId: string | number, index: number) => {
              const userIdStr = userId.toString();
              return (
                <div key={index} className="flex items-center gap-1.5">
                  <User className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-xs truncate max-w-[120px]">
                    {userMap?.[userIdStr] || `User ${userIdStr}`}
                  </span>
          </div>
              );
            })}
            {remainingCount > 0 && (
              <span className="text-xs text-muted-foreground">
                +{remainingCount} more
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: "actions",
      header: "View",
      accessor: (row: any) => row.workflow_session_id,
      width: "10%",
      align: "center" as const,
      render: (_v: any, row: any) => (
        <div className="flex justify-center">
                <Button
              variant="ghost"
              size="sm"
            className="h-8 w-8 p-0 hover:bg-[#E5E5E4] text-[#57534E] group relative"
            onClick={(e) => {
              e.stopPropagation();
              handleViewWorkflow(row);
            }}
            title="View Workflow"
          >
            <Eye className="h-4 w-4" />
                </Button>
          </div>
      ),
    },
  ];
  return cols;
  }, [userMap, disabledOverrides, togglingWorkflows, handleToggleDisabled, hasManageAccess]);

  return (
    <div className="flex-1 min-h-0 overflow-hidden flex flex-col bg-white px-8 pb-6">
      {/* Filters */}
      <div className="flex flex-wrap items-end justify-between gap-3 mb-3 flex-shrink-0">
        <div className="flex flex-wrap items-end gap-3">
          {/* Search — same width and styling as SessionsPanel for
            * cross-panel consistency. */}
          <div className="space-y-1.5 w-[220px]">
            <Label className="text-sm font-medium">Search</Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#585755] pointer-events-none" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search workflow name..."
                className="pl-8 h-[46px] bg-white border-input hover:border-[#585755]"
                aria-label="Search workflows"
              />
            </div>
          </div>

          {/* Unified User + Scheduled/Manual filter popover. The User
           *  section is searchable because the user list grows with the
           *  org. */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium opacity-0 select-none">
              Filters
            </Label>
            <FiltersPopover
              sections={[
                {
                  key: "user",
                  label: "User",
                  searchable: true,
                  searchPlaceholder: "Search users…",
                  options: uniqueUsers.map((userId) => ({
                    label: userMap?.[userId] || userId || "Anonymous",
                    value: userId,
                  })),
                  value: filterUserId,
                  onChange: setFilterUserId,
                },
                {
                  key: "trigger",
                  label: "Scheduled/Manual",
                  options: [
                    { label: "Scheduled", value: "scheduled" },
                    { label: "Manual", value: "manual" },
                  ],
                  value: filterTriggerType,
                  onChange: setFilterTriggerType,
                },
              ]}
            />
          </div>
        </div>
        <div className="min-w-[280px]">
          <DatePickerDateRange
            initialStartDate={filterStartDate}
            initialEndDate={filterEndDate}
            onApply={(start, end) => {
              setFilterStartDate(start);
              setFilterEndDate(end);
            }}
            fiscal={false}
            yearType="calendar"
          />
        </div>
      </div>

      {/* Loading Overlay */}
      {loading && (
        <div className="flex-1 min-h-0 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <SpinnerLoader className="h-8 w-8" />
            <span className="text-sm text-muted-foreground">Loading workflow runs...</span>
          </div>
        </div>
      )}

      {/* DataTable */}
      {!loading && (
        <DataTable
          className="flex-1 min-h-0"
          maxHeight="calc(100vh - 270px)"
          columns={columns}
          data={paginatedData}
          loading={false}
          emptyMessage="No workflow runs found."
          stickyHeader
          stickyFooter
          showActions={false}
          onRowClick={(row) => handleViewWorkflow(row)}
          pagination={{
            currentPage: page + 1,
            totalPages: Math.max(1, totalPages),
            pageSize: rowsPerPage,
            totalItems: filteredRuns.length,
            onPageChange: (p) => handlePageChange(p - 1),
            onPageSizeChange: (size) => handleRowsPerPageChange(String(size)),
            pageSizeOptions: [10, 20, 50, 100],
          }}
        />
      )}

      {/* Workflow Detail Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent 
          side="right" 
          className="w-[800px] sm:w-[900px] sm:max-w-4xl overflow-y-auto bg-white"
        >
          {loadingWorkflowData ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span className="ml-2 text-sm">Loading workflow details...</span>
            </div>
          ) : selectedWorkflowRun ? (
            <>
              <SheetHeader className="border-b border-[#E5E1DF] pb-4">
                <SheetTitle className="flex items-center gap-2">
                  <Workflow className="h-5 w-5" />
                  Workflow Run Details
                </SheetTitle>
                <SheetDescription className="mt-2">
                  <div className="flex flex-col gap-2">
                    {/* Workflow Name with Badges */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-[#242424]">{selectedWorkflowRun.workflow_name || "Unnamed Workflow"}</span>
                      {selectedWorkflowRun.trigger_type && (
                        <Badge
                          variant="outline"
                          className={cn(
                            "text-xs",
                            selectedWorkflowRun.trigger_type === "scheduled"
                              ? "bg-[#F5F5F4] border-[#E5E5E4] text-[#57534E]"
                              : "bg-orange-50 border-orange-200 text-orange-700"
                          )}
                        >
                          {selectedWorkflowRun.trigger_type === "scheduled" ? (
                            <Timer className="h-3 w-3 mr-1 inline" />
                          ) : (
                            <Play className="h-3 w-3 mr-1 inline" />
                          )}
                          {selectedWorkflowRun.trigger_type}
                        </Badge>
                      )}
                      {selectedWorkflowRun.workflow_agent && selectedWorkflowRun.workflow_agent !== 'default' && (
                        <Badge variant="secondary" className="bg-gray-100 text-gray-800">
                          {selectedWorkflowRun.workflow_agent === 'ga_dmg' ? 'GA DMG' 
                            : selectedWorkflowRun.workflow_agent === 'ga_affiliate' ? 'GA Affiliate'
                            : selectedWorkflowRun.workflow_agent === 'similarweb' ? 'SimilarWeb'
                            : selectedWorkflowRun.workflow_agent === 'pacing' ? 'Pacing'
                            : selectedWorkflowRun.workflow_agent === 'ga' ? 'GA'
                            : selectedWorkflowRun.workflow_agent}
                        </Badge>
                      )}
                    </div>

                    {/* Details */}
                    {selectedWorkflowRun.workflow_created_by && (
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        <span>{userMap?.[selectedWorkflowRun.workflow_created_by.toString()] || `User ${selectedWorkflowRun.workflow_created_by}`}</span>
                          </div>
                        )}
                    {selectedWorkflowRun.workflow_session_created_at && (
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        <span>{format(new Date(selectedWorkflowRun.workflow_session_created_at), "MMM dd, yyyy 'at' HH:mm")}</span>
                      </div>
                    )}
                    {selectedWorkflowRun.workflow_schedule && (
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        <span>{cronToHumanReadable(selectedWorkflowRun.workflow_schedule)}</span>
                          </div>
                        )}

                    {/* Model — workflows can store the model on the
                      * workflow row itself (workflow_metadata.model) or
                      * only on the spawned session metadata.  Try both
                      * before giving up so the panel stops looking
                      * empty for legacy workflows. */}
                    {(() => {
                      const sessionMeta = selectedWorkflowRun.session_metadata || {};
                      const wfMeta = selectedWorkflowRun.workflow_metadata || {};
                      const rawModel =
                        selectedWorkflowRun.workflow_model
                        || (typeof wfMeta.model === 'string' ? wfMeta.model : undefined)
                        || (typeof sessionMeta.model === 'string' ? sessionMeta.model : undefined);
                      const modelLabelMap: Record<string, string> = {
                        deep_analysis: 'Deep Analysis',
                        claude: 'Advanced Report Generation',
                        gemini_pro: 'Extended Analysis',
                        gemini_flash: 'Quick Analysis',
                        extended_analysis: 'Extended Analysis',
                        report_generation: 'Advanced Report Generation',
                        advanced_report_generation: 'Advanced Report Generation',
                      };
                      const modelLabel = rawModel ? (modelLabelMap[rawModel] || rawModel) : 'Default model';
                      return (
                        <div className="flex items-center gap-2">
                          <Cpu className="h-4 w-4" />
                          <Badge variant="outline" className="text-xs bg-violet-50 border-violet-200 text-violet-700">
                            {modelLabel}
                          </Badge>
                        </div>
                      );
                    })()}

                    {/* Brands — same fallback strategy as Model: prefer
                      * the workflow's own brand_map, fall back to the
                      * brand_map captured on the session that the
                      * workflow run produced. */}
                    {(() => {
                      const sessionMeta = selectedWorkflowRun.session_metadata || {};
                      const fromWorkflow = selectedWorkflowRun.workflow_brand_map;
                      const fromSession = (sessionMeta && typeof sessionMeta === 'object')
                        ? (sessionMeta as any).brand_map
                        : null;
                      const brandMap = (fromWorkflow && Object.keys(fromWorkflow).length > 0)
                        ? fromWorkflow
                        : (fromSession && typeof fromSession === 'object' ? fromSession : null);
                      if (!brandMap || Object.keys(brandMap).length === 0) {
                        return (
                          <div className="flex items-center gap-2">
                            <Tags className="h-4 w-4 flex-shrink-0" />
                            <span className="text-sm text-muted-foreground">No brand attached</span>
                          </div>
                        );
                      }
                      return (
                        <div className="flex items-center gap-2 flex-wrap">
                          <Tags className="h-4 w-4 flex-shrink-0" />
                          {Object.entries(brandMap).map(([id, name]) => (
                            <Badge key={id} variant="outline" className="text-xs bg-blue-50 border-blue-200 text-blue-700">
                              {typeof name === 'string' && name.trim() ? name : `Brand ${id}`}
                            </Badge>
                          ))}
                        </div>
                      );
                    })()}

                    {/* Shared To */}
                    {(() => {
                      const sharedIds = selectedWorkflowRun.shared_user_ids || [];
                      const notify = selectedWorkflowRun.workflow_notify;
                      if (sharedIds.length === 0 && (!notify || notify === 'none')) return null;
                      
                      return (
                        <div className="flex items-start gap-2">
                          <Share2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                          <div className="flex flex-col gap-1">
                            {/* Notification method badge */}
                            {notify && notify !== 'none' && (
                              <div className="flex items-center gap-1.5 flex-wrap">
                                {(notify === 'email' || notify === 'both') && (
                                  <Badge variant="outline" className="text-xs bg-emerald-50 border-emerald-200 text-emerald-700">
                                    <Mail className="h-3 w-3 mr-1 inline" />Email
                                  </Badge>
                                )}
                                {(notify === 'slack' || notify === 'both') && (
                                  <Badge variant="outline" className="text-xs bg-purple-50 border-purple-200 text-purple-700">
                                    <MessageSquare className="h-3 w-3 mr-1 inline" />Slack
                                  </Badge>
                                )}
                              </div>
                            )}
                            {/* Shared users */}
                            {sharedIds.length > 0 && (
                              <div className="flex items-center gap-1.5 flex-wrap">
                                {sharedIds.map((uid: string | number) => {
                                  const uidStr = uid.toString();
                                  return (
                                    <span key={uidStr} className="text-xs text-muted-foreground">
                                      {userMap?.[uidStr] || `User ${uidStr}`}
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                      </div>
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-6">
                {/* Thread History */}
                {threadHistory.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">Thread History</span>
                      <span className="text-xs text-muted-foreground">({threadHistory.length} exchanges)</span>
                    </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={collapseAllThreads}
                        className="h-7 text-xs"
                      >
                        Collapse
                      </Button>
                    </div>
                    <div className="space-y-4">
                      {threadHistory.map((thread, index) => {
                        const isExpanded = expandedThreads.has(thread.index);
                        const truncatedUser = thread.userMessage.length > 60 
                          ? thread.userMessage.substring(0, 60) + '...'
                          : thread.userMessage;
                        
                      return (
                          <div key={index} className="space-y-2">
                            {/* Collapsible Header */}
                        <button
                              onClick={() => toggleThread(thread.index)}
                              className="w-full p-2 bg-muted/50 hover:bg-muted rounded-lg transition-colors text-left flex items-center justify-between"
                            >
                              <span className="text-sm font-medium">
                                #{thread.index} {isExpanded ? thread.userMessage : truncatedUser}
                              </span>
                              <span className="text-xs text-muted-foreground ml-2">
                                {isExpanded ? 'Hide' : 'Show'}
                              </span>
                        </button>
                            
                            {/* Expanded Chat Messages */}
                            {isExpanded && (
                              <div className="space-y-3 pl-2">
                                {/* User Message */}
                                {thread.userMessage && (
                                  <div className="flex justify-end">
                                    <div className="max-w-[85%] rounded-lg p-3 bg-[#F4FFC6] border border-[#16306D]/20">
                                      <div className="flex items-center gap-2 mb-2">
                                        <User className="h-4 w-4" />
                                        <span className="text-xs font-medium capitalize">User</span>
                                        {thread.timestamp && (
                                          <span className="text-xs text-muted-foreground">
                                            {format(new Date(thread.timestamp), "HH:mm")}
                                          </span>
                                        )}
                                      </div>
                                      <div className="text-sm prose prose-sm max-w-none prose-headings:font-semibold prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 prose-table:my-2">
                                        {thread.userMessage}
                                      </div>
                                    </div>
                      </div>
                    )}
                                
                                {/* Assistant Response */}
                                {thread.assistantResponse && (
                                  <div className="flex justify-start">
                                    <div className="max-w-[85%] rounded-lg p-3 bg-muted/50 border">
                                      <div className="flex items-center gap-2 mb-2">
                                        <MessageSquare className="h-4 w-4" />
                                        <span className="text-xs font-medium capitalize">Assistant</span>
                  </div>
                                      <div className="text-sm prose prose-sm max-w-none prose-headings:font-semibold prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 prose-table:my-2">
                                        <Markdown>{thread.assistantResponse}</Markdown>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                </div>
              );
            })}
          </div>
                    {selectedWorkflowRun.session_id && (
                      <div className="mt-4 text-xs text-muted-foreground">
                        Session ID: {selectedWorkflowRun.session_id}
          </div>
        )}
      </div>
                )}

                {/* Workflow Input */}
                {(() => {
                  // Extract main_query from workflow_prompt
                  let mainQuery = selectedWorkflowRun.workflow_prompt || '';
                  
                  if (selectedWorkflowRun.workflow_prompt) {
                    try {
                      // Try to parse as JSON
                      const trimmed = selectedWorkflowRun.workflow_prompt.trim();
                      if (trimmed.startsWith('{')) {
                        const parsed = JSON.parse(trimmed);
                        mainQuery = parsed.main_query || parsed.additional_prompt || selectedWorkflowRun.workflow_prompt;
                      } else {
                        // Try to extract from XML format
                        const userPromptMatch = selectedWorkflowRun.workflow_prompt.match(/<user_prompt>\s*([\s\S]*?)\s*<\/user_prompt>/);
                        if (userPromptMatch) {
                          const extracted = userPromptMatch[1].trim();
                          if (extracted.startsWith('{')) {
                            try {
                              const parsed = JSON.parse(extracted);
                              mainQuery = parsed.main_query || parsed.additional_prompt || extracted;
                            } catch {
                              mainQuery = extracted;
                            }
                          } else {
                            mainQuery = extracted;
                          }
                        }
                      }
                    } catch (e) {
                      // If parsing fails, use the original prompt
                      mainQuery = selectedWorkflowRun.workflow_prompt;
                    }
                  }
                  
                  return mainQuery ? (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="h-6 w-6 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-xs font-bold">
                          W
              </div>
                        <span className="text-sm font-medium">Workflow Input</span>
                      </div>
                      <div className="p-3 bg-muted/50 rounded-lg border">
                        <div className="text-sm">
                          {mainQuery}
                        </div>
                      </div>
                    </div>
                  ) : null;
                })()}

                {/* Workflow Response */}
                {workflowResponse && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="h-6 w-6 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-xs font-bold">
                        A
            </div>
                      <span className="text-sm font-medium">Workflow Response</span>
                    </div>
                    <div className="p-4 bg-white rounded-lg border">
                      <div className="text-sm prose prose-sm max-w-none prose-headings:font-semibold prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 prose-table:my-2">
                        <Markdown>{workflowResponse}</Markdown>
                      </div>
          </div>
        </div>
      )}
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <Workflow className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Select a workflow run to view its details</p>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default WorkflowRunsPanel;
